import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Draggable from 'react-draggable'
import Popover from 'react-popover'
import Alert from 'react-s-alert'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Textsms from 'material-ui/svg-icons/communication/textsms'
import Email from 'material-ui/svg-icons/communication/email'
import Phone from 'material-ui/svg-icons/communication/phone'
import Close from 'material-ui/svg-icons/navigation/close'

import * as SettingsActions from '../../state/actions/message-settings'
import OffsetToXPosition from '../sitewide/OffsetToXPosition'
import buildTextEditor from './buildTextEditor'
import OffsetToNegativeXPosition from '../sitewide/OffsetToNegativeXPosition'
import XPositionToOffset from '../sitewide/XPositionToOffset'
import XPositionToNegativeOffset from '../sitewide/XPositionToNegativeOffset'
import OffsetToTime from '../sitewide/OffsetToTime'
import OffsetToNegativeTime from '../sitewide/OffsetToNegativeTime'
import OffsetToWeeksTime from '../sitewide/OffsetToWeeksTime'
import OffsetToXPositionInWeeks from '../sitewide/OffsetToXPositionInWeeks'
import XPositionToWeeksOffset from '../sitewide/XPositionToWeeksOffset'
import { logException } from '../../services/sentry'
import SettingHandleCall from '../assets/SettingHandleCall'
import SettingHandleEmail from '../assets/SettingHandleEmail'
import SettingHandleText from '../assets/SettingHandleText'
import SettingTextEditor from './SettingTextEditor'
import './styles/Handle.css'

const ERR_MESSAGES = {
  handleOffset:
    'You must drag message left or right before you are able to save.',
}

const handleAssets = {
  email: {
    icon: Email,
    handle: SettingHandleEmail,
  },
  text: {
    icon: Textsms,
    handle: SettingHandleText,
  },
  call: {
    icon: Phone,
    handle: SettingHandleCall,
  },
}

// Calling the HOC
const Editor = buildTextEditor(SettingTextEditor)

// Define two sets of labeled offsets: one for the default case and one for when isWeeks is true
const labeledOffsets = {
  default: new Set([
    3600, 7200, 25200, 21600, 172800, 1209600, 1382400, 2505600, 86400, 1296000,
    2592000,
  ]),
  weeks: new Set([
    // Assuming these are the offsets in seconds for weeks
    // 604800, 1209600, 1814400, 2419200, 3024000, 3628800,
  ]),
};

function getLabeledOffsets(isWeeks) {
  return isWeeks ? labeledOffsets.weeks : labeledOffsets.default;
}

class Handle extends Component {
  static propTypes = {
    activateSetting: PropTypes.func,
    activeLocation: PropTypes.shape({
      active: PropTypes.bool.isRequired,
      additional_recipients: PropTypes.arrayOf(PropTypes.object),
      city: PropTypes.string,
      country_code: PropTypes.string,
      created_at: PropTypes.string,
      doctor_id: PropTypes.string,
      earliest_same_day_reminder_hour: PropTypes.number,
      facebook_url: PropTypes.string,
      forwarding_email: PropTypes.string,
      google_url: PropTypes.string,
      id: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      name: PropTypes.string,
      office_email: PropTypes.string,
      office_phone: PropTypes.string,
      phone_numbers: PropTypes.arrayOf(PropTypes.object),
      practice_id: PropTypes.number,
      state: PropTypes.string,
      street_address: PropTypes.string,
      timezone: PropTypes.string,
      updated_at: PropTypes.string,
      website: PropTypes.string,
      yelp_url: PropTypes.string,
      zip: PropTypes.string,
    }),
    attrForNewSetting: PropTypes.shape({
      tmpId: PropTypes.number.isRequired,
    }),
    createMessageSetting: PropTypes.func,
    deactivateSetting: PropTypes.func,
    hasPlum: PropTypes.bool,
    hasErrors: PropTypes.bool,
    handleSnackbarOpen: PropTypes.func,
    initialXPosition: PropTypes.number.isRequired,
    isNegative: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool,
    isPopoverOpen: PropTypes.bool,
    maxChars: PropTypes.number.isRequired,
    pmsType: PropTypes.string,
    pmsVersion: PropTypes.number,
    receiveMessageSettings: PropTypes.func,
    removeLocalHandle: PropTypes.func,
    setting: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        offset: PropTypes.number.isRequired,
      }),
    ]),
    settingId: PropTypes.number,
    sliderId: PropTypes.number,
    sliderName: PropTypes.string,
    updateMessageSetting: PropTypes.func,
    updatePopoverState: PropTypes.func,
    isWeeks: PropTypes.bool.isRequired,
  }

  constructor(props) {
    super(props)
    const {
      isOpen,
      setting: { offset },
      settingId,
      initialXPosition,
    } = props
    this.state = {
      deltaPosition: {
        x: initialXPosition,
        y: 0,
      },
      handleOffset: offset, // 'fork' state with props
      isOpen: !!isOpen, // false unless specifically passed as prop (ie: when adding new handle)
      isNewHandle: !settingId, // won't have an ID if not stored in DB yet
      isSaved: true,
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.isPopoverOpen) this.closePopover()
  }

  setToInactive = () => {
    const {
      activateSetting,
      attrForNewSetting,
      deactivateSetting,
      removeLocalHandle,
      settingId,
    } = this.props
    if (this.state.isNewHandle) {
      removeLocalHandle(attrForNewSetting.tmpId)
      // no undo for you!
    } else {
      deactivateSetting(settingId).catch(err => {
        activateSetting(settingId, { dispatchOnly: true })
        logException(err)
      })
    }
  }

  // only for creating a new setting in API
  createSetting = ({ body, subject, always_send }) => {
    const { handleOffset } = this.state
    const {
      attrForNewSetting,
      createMessageSetting,
      removeLocalHandle,
      sliderId,
      activeLocation,
    } = this.props

    if (!handleOffset) {
      // do not let user save offset of 0
      const msg = ERR_MESSAGES.handleOffset
      return this.handleError(msg)
    }

    const changes = {
      offset: handleOffset,
      body,
      subject,
      always_send,
      custom_setting_id: sliderId,
    }
    if (activeLocation) changes.location_id = activeLocation.id

    return createMessageSetting(attrForNewSetting, changes)
      .then(() => {
        this.setState(
          {
            isEditing: false,
            isSaved: true,
            isNewHandle: false,
          },
          () => {
            this.closePopover()
            removeLocalHandle(attrForNewSetting.tmpId)
          },
        )
      })
      .catch(err => {
        logException(err)
        // TODO: damn this is fragile, figure out if error could be closer to
        // response's root.
        // return this.handleError(err.body.error.practice[0][0])
        if (err.body.error) {
          return this.handleError(Object.values(err.body.error).join(' '))
        }
        return this.handleError(err.statusText)
      })
  }

  updateSetting = ({ body, subject, always_send }) => {
    // TODO: move the fetch calls (POST, PATCH) in here. be sure to pass in
    // the template content (body and/or subject) before sending it to API.
    const { handleOffset } = this.state
    const { updateMessageSetting, settingId } = this.props

    if (!handleOffset) {
      // do not let user save offset of 0
      const msg = ERR_MESSAGES.handleOffset
      return this.handleError(msg)
    }

    const changes = {
      offset: handleOffset,
      body,
      subject,
      always_send,
    }

    return updateMessageSetting(settingId, changes)
      .then(() => {
        this.setState(
          {
            isEditing: false,
            isSaved: true,
          },
          () => {
            this.closePopover()
          },
        )
      })
      .catch(err => {
        logException(err)
        // TODO: damn this is fragile, figure out if error could be closer to
        // response's root.
        // return this.handleError(err.body.error.practice[0][0])
        return this.handleError(err.statusText)
      })
  }

  handleDrag = (e, ui) => {
    const { x } = this.state.deltaPosition
    const { isNegative, isWeeks } = this.props

    const nextX = x + ui.deltaX
    let nextOffset
    if (isWeeks) {
      nextOffset = XPositionToWeeksOffset[nextX]
    } else if (isNegative) {
      nextOffset = XPositionToNegativeOffset[nextX]
    } else {
      nextOffset = XPositionToOffset[nextX]
    }

    this.setState({
      deltaPosition: {
        x: nextX,
        y: 0,
      },
      handleOffset: nextOffset,
      isSaved: false,
    })
  }

  makeChanges = () => {
    this.setState({ isSaved: false })
  }

  discardDrag = () => {
    this.setState({
      handleOffset: this.props.setting.offset,
      deltaPosition: {
        x: this.props.initialXPosition,
        y: 0,
      },
      isSaved: true,
    })
  }

  closePopover = () => {
    const { updatePopoverState } = this.props
    this.setState({
      isOpen: false,
    })
    updatePopoverState(false)
  }

  handleError = msg => {
    // render error
    Alert.warning(msg, {
      position: 'top-right',
      effect: 'jelly',
      timeout: 5000,
    })
    // chain to caller
    return Promise.reject(msg)
  }

  handleStop = () => {
    const { isPopoverOpen } = this.props
    if (!isPopoverOpen) {
      this.setState({ isOpen: true })
      this.props.updatePopoverState(true)
    }
  }

  offsetText = () => {
    const { isNegative, isWeeks } = this.props
    const { handleOffset } = this.state
    // check for if reminder is on pre-labeled date
    if (getLabeledOffsets(isWeeks).has(Math.abs(handleOffset))) {
      return '';
    }

    // if it's not a pre-labeld date
    if (isWeeks) {
      return OffsetToWeeksTime[handleOffset];
    } else {
      return isNegative
        ? OffsetToNegativeTime[handleOffset]
        : OffsetToTime[handleOffset];
    }
  }

  render() {
    const { isOpen, isSaved, handleOffset } = this.state
    const {
      handleSnackbarOpen,
      hasErrors,
      hasPlum,
      isPostAppt,
      isWeeks,
      maxChars,
      setting,
      pmsType,
      pmsVersion,
      setting: {
        id,
        message_type: messageType,
        body,
        subject,
        always_send,
        message_setting_type: messageSettingType,
      },
    } = this.props
    const isEmail = messageType === 'email'
    const isReminder =
      messageSettingType === 'reminder' ||
      messageSettingType === 'custom' ||
      messageSettingType === 'custom_recall'
    // cloud9 recall appointments have the same info as all other appointment types
    const isRecall = messageSettingType === 'recall' && pmsType !== 'Cloud9'

    const editorProps = {
      setting,
      offset: handleOffset,
      closePopover: this.closePopover,
      updateSetting: this.updateSetting,
      createSetting: this.createSetting,
      handleSnackbarOpen,
      setToInactive: this.setToInactive,
      attrForNewSetting: id ? null : this.props.setting,
      isEmail,
      isPostAppt,
      isWeeks,
      isReminder,
      isRecall,
      hasPlum,
      shouldAlwaysSend: always_send,
      isNegative: !!this.props.isNegative,
      discardDrag: this.discardDrag,
      hasMoved: !isSaved,
      isMessageSetting: true,
      maxChars,
      pmsType,
      pmsVersion,
    }

    const editor = isEmail ? (
      <Editor {...editorProps} body={body} subject={subject} />
    ) : (
      <Editor {...editorProps} makeChanges={this.makeChanges} body={body} />
    )

    const ReminderIcon = isSaved ? handleAssets[messageType].icon : Close

    const SettingHandle = handleAssets[messageType].handle

    const characterCount =
      body.text !== undefined ? body.text.replace(/@/g, '').length : 0
    const exceedsMaxChars = characterCount > maxChars && !isEmail

    return (
      <Popover isOpen={isOpen} className="handlePopover" body={editor}>
        <Draggable
          axis="x"
          handle=".handleContainer"
          defaultPosition={{ x: this.props.initialXPosition, y: 0 }}
          position={this.state.deltaPosition}
          bounds={{ left: 0, right: 814 }}
          grid={[22, 0]}
          zIndex={100}
          onStart={this.handleStart}
          onDrag={this.handleDrag}
          onStop={this.handleStop}
        >
          <div className={`handleContainer ${messageType}-reminder`}>
            <div className={classnames('arrowBox', { unsaved: !isSaved })}>
              <SettingHandle
                className={classnames('handleShape', {
                  exceedsMaxChars,
                  handleError: hasErrors,
                })}
              />
              <ReminderIcon
                className={classnames('handleIcon', {
                  handleError: hasErrors,
                })}
              />
            </div>
            <div className="handle" />

            <div className="handleOffset">
              <span className="offsetText">{this.offsetText()}</span>
            </div>
          </div>
        </Draggable>
      </Popover>
    )
  }
}

// represent areas in the number line that are labeled. The units are the number
// of seconds (offset values).

const mapStateToProps = (
  {
    account: {
      auth: { hasPlum, pmsType, pmsVersion },
      user: { activeLocation, countryCode },
    },
    messageSettings,
  },
  ownProps,
) => {
  const { settingId, isNegative, isWeeks, attrForNewSetting } = ownProps
  const setting = settingId
    ? messageSettings.byId[settingId]
    : attrForNewSetting // any new message settings w/o an assigned id

  let offsetTable
  if (isWeeks) {
    offsetTable = OffsetToXPositionInWeeks
  } else {
    offsetTable = isNegative ? OffsetToNegativeXPosition : OffsetToXPosition
  }

  const maxChars = ['US', 'CA'].includes(countryCode) ? 460 : 300

  return {
    activeLocation,
    isNegative,
    initialXPosition: offsetTable[setting.offset],
    hasPlum,
    maxChars,
    pmsType,
    pmsVersion,
    settingId,
    setting,
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      ...SettingsActions,
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(Handle)



// WEBPACK FOOTER //
// ./src/components/Settings/Handle.js